import {
  Box,
  Flex,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text
} from '@chakra-ui/react';
import React from 'react';

import type { PatientInfoData } from '../../interface/patientInfo';
import type { SlotProps } from '../../interface/slotProps';
import {
  INK,
  INK_MUTED,
  INK_SOFT,
  POPOVER_BORDER,
  POPOVER_RADIUS,
  POPOVER_SHADOW,
  SURFACE_WARM
} from '../../styles/tokens';

const INTER_STACK = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const HOVER_HINT_SX = {
  cursor: 'help',
  textDecoration: 'underline dotted var(--border-strong, #c9c8ce)',
  textUnderlineOffset: '3px',
  '&:hover, &:focus-visible': {
    textDecorationColor: 'var(--text-muted, #7a7682)'
  }
} as const;

const na = (v: unknown): string => typeof v === 'string' || typeof v === 'number' ? String(v) : 'N/A';

const MONTHS_ABBR = [ 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec' ] as const;

/**
 * Reformats a stored date of birth from ISO `YYYY-MM-DD` (optionally with a trailing time component,
 * which is ignored) into the human `MMM D, YYYY` display form, e.g. `'1945-08-03'` -> `'Aug 3, 1945'`
 * (no leading zero on the day). Deliberately parses the string's own numeric parts rather than
 * `new Date(iso)`: `new Date('1945-08-03')` is UTC midnight, so `toLocaleDateString` in any timezone
 * behind UTC would render the PREVIOUS calendar day -- a date of birth must never drift by a day.
 *
 * Stays inside `PatientInfo`'s isolation boundary (no `date-fns`/`dayjs` import -- neither is on the
 * `project_starburst` allowlist). Fails soft in the same spirit as `na()`: a non-string (absent /
 * malformed shape) returns `undefined` so the row falls back to `"N/A"`; a present string that is not
 * ISO-shaped or carries an out-of-range month/day is returned VERBATIM rather than dropped, so a real
 * (if unexpected) stored value is never silently lost.
 */
const formatDob = (raw: unknown): string | undefined => {
  if (typeof raw !== 'string') return undefined;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw.trim());
  if (!match) return raw;
  const [, year, month, day] = match;
  const monthIndex = Number(month) - 1;
  const dayNumber = Number(day);
  if (monthIndex < 0 || monthIndex > 11 || dayNumber < 1 || dayNumber > 31) {
    return raw;
  }
  return `${MONTHS_ABBR[monthIndex]} ${dayNumber}, ${year}`;
};

/** One popover-body row: a stable kebab-case id (stamped as `data-testid="patient-info-<id>"`) +
 *  its label + the raw (possibly absent) field value -- `na()` is applied once, at render, not
 *  baked into this table. */
interface PatientInfoRow {
  readonly id: string;
  readonly label: string;
  readonly value?: string;
}

/**
 * Renders one label/value popover row: a flex row, label left / value right,
 * `justifyContent="space-between"`, per the design. Label and value share the app-default font;
 * the value is set apart by its role weight and color (`INK`). Sizing matches `CaseSpecialty`'s
 * popover rows (11px/11.5px) rather than the `Caption 1`/`Caption 2` textStyle tokens, which
 * render slightly larger and made this popover look bigger than CaseSpecialty's.
 */
const PatientInfoRowView: React.FC<{
  readonly row: PatientInfoRow;
}> = ({ row }) => (
  <Flex
    justifyContent="space-between"
    gap="20px"
    lineHeight="1.6"
    data-testid={`patient-info-${row.id}`}
  >
    <Text as="span" fontSize="11px" fontWeight={400} color={INK_SOFT}>
      {row.label}
    </Text>
    <Text as="span" fontSize="11.5px" fontWeight={600} color={INK}>
      {na(row.value)}
    </Text>
  </Flex>
);

export const PatientInfo: React.FC<SlotProps> = ({ value }) => {
  const data: PatientInfoData =
    value != null && typeof value === 'object' && !Array.isArray(value)
      ? (value as PatientInfoData)
      : {};

  const genderPart = na(data.gender);
  const agePart =
    typeof data.age === 'string' || typeof data.age === 'number'
      ? `${data.age} yrs`
      : 'N/A';

  const nameRows: readonly PatientInfoRow[] = [
    { id: 'full-name', label: 'Name', value: data.fullName },
    { id: 'dob', label: 'Date of birth', value: formatDob(data.dob) }
  ];
  const identifierRows: readonly PatientInfoRow[] = [
    { id: 'mrn', label: 'MRN', value: data.mrn },
    { id: 'account-number', label: 'Account #', value: data.accountNumber },
    { id: 'visit-number', label: 'Visit #', value: data.visitNumber },
    { id: 'case-id', label: 'Case ID', value: data.caseId }
  ];

  return (
    <Popover trigger="hover" placement="bottom-start">
      <PopoverTrigger>
        <Text
          as="span"
          tabIndex={0}
          aria-label={`Patient information: age ${na(data.age)}, gender ${na(data.gender)}`}
          data-testid="patient-info"
          fontFamily={INTER_STACK}
          fontSize="12px"
          fontWeight={500}
          color={INK}
          sx={HOVER_HINT_SX}
        >
          {genderPart}, {agePart}
        </Text>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          data-testid="patient-info-popover"
          bg={SURFACE_WARM}
          border={`1px solid ${POPOVER_BORDER}`}
          borderRadius={POPOVER_RADIUS}
          boxShadow={POPOVER_SHADOW}
          padding="11px 13px"
          width="226px"
        >
          <Box
            textStyle="Overline 1"
            color={INK_MUTED}
            textTransform="uppercase"
            marginBottom="8px"
          >
            Patient
          </Box>
          <Flex direction="column" gap="6px">
            {nameRows.map((row) => (
              <PatientInfoRowView key={row.id} row={row} />
            ))}
          </Flex>
          <Box
            borderTop={`1px solid ${POPOVER_BORDER}`}
            marginTop="8px"
            paddingTop="8px"
          >
            <Flex direction="column" gap="6px">
              {identifierRows.map((row) => (
                <PatientInfoRowView key={row.id} row={row} />
              ))}
            </Flex>
          </Box>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
